import { Link, useNavigate } from "react-router-dom";
import { Trash2, Minus, Plus, ShoppingBag } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { resolveImg } from "@/components/ProductCard";

const SHIPPING = 9.99;

export default function CartPage() {
  const { items, updateQty, removeItem, subtotal } = useCart();
  const navigate = useNavigate();

  if (items.length === 0) {
    return (
      <div className="mx-auto flex max-w-2xl flex-col items-center px-6 py-32 text-center">
        <ShoppingBag className="mb-6 h-12 w-12 text-neutral-400" strokeWidth={1} />
        <h1 className="mb-3 text-3xl font-bold text-[#2b2520]">Your cart is empty</h1>
        <p className="mb-8 text-neutral-600">Add some flower or prerolls to get started.</p>
        <Link
          to="/shop"
          data-testid="cart-shop-link"
          className="bg-[#c99a63] px-8 py-4 text-sm font-semibold uppercase tracking-widest text-[#2b2520] transition-colors hover:bg-[#bb8850]"
        >
          Browse products
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl px-6 py-16">
      <h1 className="mb-10 text-4xl font-black text-[#2b2520]">Your Cart</h1>
      <div className="grid gap-12 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2" data-testid="cart-items">
          {items.map((item) => (
            <div
              key={item.key}
              data-testid={`cart-row-${item.key}`}
              className="flex gap-4 border border-black/10 bg-white p-4"
            >
              <img
                src={resolveImg(item.product.image_url)}
                alt={item.product.name}
                className="h-24 w-24 flex-shrink-0 object-cover"
              />
              <div className="flex flex-1 flex-col justify-between">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-bold text-[#2b2520]">{item.product.name}</h3>
                    <p className="text-xs text-neutral-500">{item.sizeLabel}</p>
                  </div>
                  <button
                    data-testid={`cart-remove-${item.key}`}
                    onClick={() => removeItem(item.key)}
                    className="text-neutral-500 transition-colors hover:text-[#d32f2f]"
                  >
                    <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                  </button>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center border border-black/15">
                    <button
                      onClick={() => updateQty(item.key, item.quantity - 1)}
                      className="px-3 py-2 text-[#2b2520] hover:text-[#a06d34]"
                    >
                      <Minus className="h-3 w-3" strokeWidth={1.5} />
                    </button>
                    <span className="w-8 text-center text-sm text-[#2b2520]">{item.quantity}</span>
                    <button
                      onClick={() => updateQty(item.key, item.quantity + 1)}
                      className="px-3 py-2 text-[#2b2520] hover:text-[#a06d34]"
                    >
                      <Plus className="h-3 w-3" strokeWidth={1.5} />
                    </button>
                  </div>
                  <span className="font-semibold text-[#2b2520]">
                    ${(item.unitPrice * item.quantity).toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="h-fit border border-black/10 bg-white p-8">
          <h2 className="mb-6 text-lg font-bold text-[#2b2520]">Order Summary</h2>
          <div className="space-y-3 border-b border-black/10 pb-4 text-sm">
            <div className="flex justify-between text-neutral-600">
              <span>Subtotal</span>
              <span data-testid="cart-subtotal" className="text-[#2b2520]">${subtotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-neutral-600">
              <span>Shipping</span>
              <span className="text-[#2b2520]">${SHIPPING.toFixed(2)}</span>
            </div>
          </div>
          <div className="my-4 flex justify-between text-lg font-semibold text-[#2b2520]">
            <span>Total</span>
            <span data-testid="cart-total">${(subtotal + SHIPPING).toFixed(2)}</span>
          </div>
          <button
            data-testid="checkout-btn"
            onClick={() => navigate("/checkout")}
            className="w-full bg-[#c99a63] py-4 text-sm font-semibold uppercase tracking-widest text-[#2b2520] transition-colors hover:bg-[#bb8850]"
          >
            Proceed to checkout
          </button>
        </div>
      </div>
    </div>
  );
}
